import React, { useEffect, useRef, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Avatar from 'react-avatar';
import { updateSelectedCandidate } from '../../store/global/actions';

import { useChat } from '../../hooks/useChat';
import { useMessages } from '../../hooks/useMessages';
import MessagesList from '../messages/MessagesList';
import { SOCKET_EVENT_NAME } from '../../constants/constants';
import MessageInput from '../messages/MessageInput';
import { MSG_USER_ROLE, createMessageData, handleSocketResponse } from '../../utils/messageHelpers';

import { createPortal } from 'react-dom';

function SendMessage(props) {
  const headerRef = useRef(null);
  const chatEndRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const [error, setError] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const { selectedCandidate, updateSelectedCandidate, headerData } = props;
  const location = useLocation();
  const { chatState, setChatState, isLoadingRef, emit, SCROLL_THRESHOLD } = useChat(() => null);
  const { loadInitialMessages, loadPreviousMessages, scrollToBottom } = useMessages(
    chatState,
    setChatState,
    isLoadingRef,
    messagesScrollRef
  );

  useEffect(() => {
    if (selectedCandidate?.type === 'messages') {
      setError('');
      getChats();
      setIsMinimized(false);
    }
    return () => emit(SOCKET_EVENT_NAME.CHAT_CLOSE);
  }, [selectedCandidate]);

  useEffect(() => {
    updateSelectedCandidate(null);
  }, [location.pathname]);

  const getChats = async () => {
    isLoadingRef.current = false;
    setChatState((prev) => ({
      ...prev,
      activeChatId: selectedCandidate.id,
      activeChatInfo: {
        first_name: selectedCandidate.details.name,
        last_name: '',
        image: selectedCandidate?.details?.image
      },
      newMessage: '',
      currentMessages: [],
      currentPage: 1,
      canLoadMore: true
    }));
    loadInitialMessages(selectedCandidate.id, true);
    emit(SOCKET_EVENT_NAME.CHAT_OPEN, selectedCandidate.id);
  };

  const handleClose = () => {
    setError('');
    updateSelectedCandidate(null);
  };

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
    if (isMinimized) {
      scrollToBottom();
    }
  };

  const onScrollToTop = (e) => {
    const { scrollTop } = e.target;
    if (scrollTop <= SCROLL_THRESHOLD && chatState.canLoadMore && !isLoadingRef.current) {
      loadPreviousMessages();
    }
  };
  const sendMessage = (messageData) => {
    return new Promise((resolve, reject) => {
      emit(SOCKET_EVENT_NAME.SEND_MESSAGE, messageData, (response) => {
        try {
          const result = handleSocketResponse(response);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      });
    });
  };
  const handleMessageChange = (value) => {
    if (error?.length) {
      setError('');
    }
    setChatState((prev) => ({ ...prev, newMessage: value }));
  };
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!chatState.newMessage.trim() || !chatState.activeChatId) {
      setError('Please enter a message');
      return;
    }
    const messagetext = chatState?.newMessage?.replace(/<p><\/p>/g, '<p>&nbsp;</p>');
    const messageData = createMessageData(
      chatState.activeChatId,
      messagetext,
      chatState.activeChatInfo
    );
    const result = await sendMessage(messageData);

    if (typeof result === 'string') return setError(result);
    else setError('');
    setChatState((prev) => ({
      ...prev,
      currentMessages: [...prev.currentMessages, result],
      newMessage: ''
    }));
    scrollToBottom();
  };

  return createPortal(
    <>
      {selectedCandidate?.type === 'messages' && (
        <div
          className="small-chat"
          style={{
            position: 'fixed',
            zIndex: '1000',
            right: '20px',
            bottom: '20px',
            width: '400px'
          }}>
          <div className="card rounded-4 overflow-hidden shadow-lg">
            <div ref={headerRef} className="card-header">
              <ul className="list-style-none p-0 m-0 d-flex justify-content-between">
                <li className="d-flex align-items-center">
                  <Avatar
                    size="50"
                    round
                    name={selectedCandidate?.details?.name}
                    src={selectedCandidate?.details?.image}
                    className="object-fit-cover rounded-pill me-3"
                  />
                  <div>
                    <h6 className="mb-0 lh-base fw-600 text-dark text-opacity-75 text-capitalize">
                      {selectedCandidate?.details.name}
                    </h6>
                    {selectedCandidate?.details?.jobTitle && (
                      <div className="fs-14 lh-base text-dark text-opacity-75 text-capitalize">
                        {selectedCandidate?.details.jobTitle}
                      </div>
                    )}
                    {selectedCandidate?.details?.location && (
                      <p className="fs-14 lh-base text-secondary fw-500 text-capitalize mb-0">
                        {selectedCandidate?.details?.location}
                      </p>
                    )}
                  </div>
                </li>
                <li className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="border-0 bg-transparent d-flex p-1 rounded"
                    onClick={handleMinimize}
                    style={{
                      cursor: 'pointer'
                    }}
                    title={isMinimized ? 'Expand chat' : 'Minimize chat'}>
                    <i
                      className={`${
                        isMinimized ? 'fa-solid fa-angle-up' : 'fa-solid fa-angle-down'
                      } fs-4 text-secondary`}></i>
                  </button>
                  <button
                    type="button"
                    className="border-0 bg-transparent d-flex p-1 rounded"
                    onClick={handleClose}
                    style={{
                      cursor: 'pointer'
                    }}
                    title="Close chat">
                    <i className="fa fa-close fs-4 text-secondary"></i>
                  </button>
                </li>
              </ul>
            </div>

            {!isMinimized && (
              <div className="position-relative">
                <MessagesList
                  chatState={chatState}
                  headerData={headerData}
                  messagesScrollRef={messagesScrollRef}
                  chatEndRef={chatEndRef}
                  onScrollToTop={onScrollToTop}
                  isMini
                />

                <div>
                  <MessageInput
                    newMessage={chatState.newMessage}
                    onMessageChange={handleMessageChange}
                    onSendMessage={handleSendMessage}
                    error={error}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>,
    document.body
  );
}

export default connect(
  (state) => ({
    selectedCandidate: state.global.selectedCandidate,
    headerData: state.employer.headerData
  }),
  { updateSelectedCandidate }
)(SendMessage);
